



















import { defineComponent, ref, computed, watch, onBeforeUnmount, onMounted } from '@vue/composition-api';
import useVuelidate from '@vuelidate/core';
import PatchedVTextField from '@berlin-front/common/components/elements/PatchedVTextField';
import { ThemeContext } from '../../../services/ThemeContext';
import { toHankakuNumber } from '../../../utils';
import { useContext } from '../../../composables/context';
import useId from '../../../composables/useId';
import { getErrors, regexp } from '../../../composables/validator';

export default defineComponent({
  name: 'AuthCodeInput',
  components: {
    PatchedVTextField,
  },

  emits: [
    /**
     * 最後の入力欄に入力がなされたタイミングで、かつ、入力エラーがない時に発火する。
     */
    'completed',
  ],

  setup(props, { emit }) {
    const { themeParameter } = useContext(ThemeContext);
    const primaryColor = computed(() => themeParameter.value.primaryColor);
    const text = ref('');

    /** vuetifyの生成するinput要素に設定するID。 */
    const id = useId();
    /**
     * このコンポーネントのルートエレメント。
     * mutation observerでDOMツリーの変更をウォッチするために使う。
     */
    const elementRef = ref<HTMLElement>();
    /**
     * vuetifyの生成するinput要素に直接仕掛けるイベントハンドラ。
     * 入力文字数に制限をかける。
     * @param ev
     */
    const onRawInput = (ev: Event) => {
      const { activeElement } = document;
      if (
        ev.target instanceof HTMLInputElement &&
        ev instanceof InputEvent &&
        activeElement instanceof HTMLInputElement &&
        activeElement.selectionEnd === activeElement.selectionStart &&
        typeof activeElement.selectionEnd === 'number'
      ) {
        const pos = activeElement.selectionEnd;
        const right = toHankakuNumber(activeElement.value.slice(pos)) || '';
        const left = toHankakuNumber(activeElement.value.slice(0, Math.min(pos, 6 - right.length))) || '';
        // IMEを確定させるためにblur->再focusする。
        activeElement.blur();
        ev.target.focus();
        // 半角数のみにしてき戻しを行う。
        text.value = left + right;
        activeElement.value = text.value;
        // カーソル位置を戻す。入力イベント直後は、ドコも選択されてないからendとstartは同じ位置になる。
        activeElement.selectionEnd = left.length;
        activeElement.selectionStart = left.length;
      }
    };
    /**
     * vuetifyの生成するinput要素に仕掛けたイベントハンドラを解除するためのコールバック。
     */
    let removeHandler = () => {};
    /**
     * 当コンポーネントのDOMツリーが変化した時に呼ばれる。
     * MutationObserverに仕掛けられるコールバック。
     * vuetifyのinput要素が生成された時に、イベントハンドラを仕掛ける役割。
     */
    const callback = () => {
      const inputElement = document.getElementById(id);
      if (inputElement instanceof HTMLInputElement) {
        removeHandler();
        // キャプチャフェーズを使用することで、vuetifyのコンポーネントのイベントハンドラよりも早い段階でイベントを補足する。
        // vuetify内のinputイベントが発火する前に、全角->半角変換とか数字以外の文字の削除とかやってしまう狙い。
        // これやらないと、text.valueと表示の同期が崩れちゃう。
        inputElement.addEventListener('input', onRawInput, { capture: true });
        removeHandler = () => inputElement.removeEventListener('input', onRawInput);
      }
    };
    const config: MutationObserverInit = {
      subtree: true,
      childList: true,
    };
    const observer = new MutationObserver(callback);
    onMounted(() => {
      if (elementRef.value) {
        // マウント直後にオブサーバーと、可能であればvuetifyのinput要素に対してイベントハンドラをセットする。
        observer.observe(elementRef.value, config);
        callback();
      }
    });
    onBeforeUnmount(() => {
      // - 入力要素に対して仕掛けたイベントハンドラの介助
      // - observerでの監視の終了。
      removeHandler();
      observer.disconnect();
    });

    const regexpCond = regexp(/^\d{0,6}$/, '数字を入力してください');
    const rules = {
      text: {
        regexp: regexpCond,
      },
    };
    const v$ = useVuelidate(rules, { text });
    const error = computed(() => getErrors(v$)('text'));

    // 認証番号が6桁に達したら、`completed`を発火する
    watch(text, (value) => {
      if (typeof value === 'string') {
        if (value.length === 6) {
          emit('completed', value);
        }
      }
    });

    const appInputRef = ref();
    const clearNumbers = () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      text.value = '';
    };

    // smsコード入力待ち受け(Android)
    const abortController =
      'AbortController' in window
        ? new AbortController()
        : // AbortControllerが存在しない環境では、OTPCredentialは使えないはずなので、こんな感じでごまかす。polyfill無駄に入れる必要はない。
          { abort: () => {}, signal: { aborted: true } as AbortSignal };
    onBeforeUnmount(() => abortController.abort());
    void (async (): Promise<void> => {
      if ('OTPCredential' in window) {
        while (!abortController.signal.aborted) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const otp = await navigator.credentials.get({
              otp: { transport: ['sms'] },
              signal: abortController.signal,
            });
            if (otp?.code && /^\d{6}$/.test(otp?.code)) {
              text.value = otp?.code;
            }
          } catch (e) {
            // 握り潰し。
            break;
          }
        }
      }
    })();

    return { elementRef, id, v$, text, error, clearNumbers, appInputRef, primaryColor };
  },
});
